import { useEffect, useState, useCallback } from "react";
import { useActivityTracking } from "../../../hooks/userActivityTracking";
import { formatAxiosError } from "../../../utils/error_message";
import { isAllowedFileExtension } from "../utils/fileValidation";
import { triggerBlobDownload } from "../utils/downloadFile";
import { useStepper } from "./useStepper";

const DEFAULT_ALLOWED_EXTENSIONS = ["xlsx"];
const UPLOAD_PROGRESS_INTERVAL_MS = 200;
const UPLOAD_PROGRESS_STEP = 20;
const TOTAL_STEPS = 3;

export function useFileProcessor({
  processFn,
  activityKey,
  accountId,
  downloadFileName = "Processed_output.xlsx",
  outputMimeType = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  allowedExtensions = DEFAULT_ALLOWED_EXTENSIONS,
}) {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [uploadComplete, setUploadComplete] = useState(false);
  const [processedBlobUrl, setProcessedBlobUrl] = useState(null);
  const [processComplete, setProcessComplete] = useState(false);

  /** useState calls + manual Set manipulation in this file. currentStep and
  completedSteps below come straight from the hook.*/
  const {
    currentStep,
    completedSteps,
    completeStep,
    reset: resetStepper,
  } = useStepper(TOTAL_STEPS);

  const { trackAction, resetSession } = useActivityTracking(
    activityKey,
    accountId,
  );

  useEffect(() => {
    if (uploadComplete) completeStep(1);
  }, [uploadComplete, completeStep]);

  useEffect(() => {
    if (processComplete) completeStep(2);
  }, [processComplete, completeStep]);

  // Revoke the blob URL when it's replaced or the component unmounts, to avoid leaking memory
  useEffect(() => {
    return () => {
      if (processedBlobUrl) window.URL.revokeObjectURL(processedBlobUrl);
    };
  }, [processedBlobUrl]);

  const goBackToStep1 = useCallback(() => {
    setFile(null);
    setUploadComplete(false);
    setProgress(0);
    setProcessedBlobUrl(null);
    setProcessComplete(false);
    resetStepper();
    resetSession();

    const fileInput = document.getElementById("file-upload");
    if (fileInput) fileInput.value = "";
  }, [resetSession, resetStepper]);

  const processFile = useCallback(async () => {
    if (!file) return;
    setLoading(true);
    try {
      const response = await processFn(file);
      const blob = new Blob([response.data], { type: outputMimeType });
      const url = window.URL.createObjectURL(blob);
      setProcessedBlobUrl(url);
      setProcessComplete(true);
      await trackAction("Process File", "SUCCESS", null, {
        fileName: file?.name,
      });
    } catch (error) {
      const { serverMessage, fullError } = await formatAxiosError(error);
      alert(serverMessage ?? "Failed to process file.");
      await trackAction("Process File", "FAILED", fullError, {
        fileName: file?.name,
      }).catch(console.error);
    } finally {
      setLoading(false);
    }
  }, [file, processFn, outputMimeType, trackAction]);

  const handleDownload = useCallback(() => {
    if (!processedBlobUrl || !file) return;
    triggerBlobDownload(processedBlobUrl, downloadFileName);
    trackAction("Download Processed File", "SUCCESS", null, {
      fileName: file?.name,
      downloadFileName,
    }).catch(console.error);
  }, [processedBlobUrl, file, downloadFileName, trackAction]);

  const handleCompletedSteps = useCallback(async () => {
    handleDownload();
    completeStep(3);
  }, [handleDownload, completeStep]);

  const handleFileUpload = useCallback(
    (event) => {
      const uploadedFile = event.target.files[0];
      if (!uploadedFile) return;

      if (!isAllowedFileExtension(uploadedFile.name, allowedExtensions)) {
        alert(
          `Only ${allowedExtensions.map((e) => `.${e}`).join(", ")} files are allowed!`,
        );
        return;
      }

      setFile(uploadedFile);
      setUploadComplete(false);
      setProcessComplete(false);
      setProgress(0);
      setProcessedBlobUrl(null);
      setLoading(true);

      const progressInterval = setInterval(() => {
        setProgress((prev) => {
          if (prev >= 100) {
            clearInterval(progressInterval);
            setUploadComplete(true);
            setLoading(false);
            return 100;
          }
          return prev + UPLOAD_PROGRESS_STEP;
        });
      }, UPLOAD_PROGRESS_INTERVAL_MS);
    },
    [allowedExtensions],
  );

  return {
    file,
    loading,
    progress,
    uploadComplete,
    processComplete,
    currentStep,
    completedSteps,
    handleFileUpload,
    processFile,
    handleDownload,
    handleCompletedSteps,
    goBackToStep1,
  };
}
